/**
 * Utility functions for file type detection and handling
 */

/**
 * Check if a file is a PDF based on its filename
 */
export const isPdfFile = (filename: string): boolean => {
	// Check by file extension
	const extension = filename.split('.').pop()?.toLowerCase();
	return extension === 'pdf';
};

/**
 * Check if a file is an Office document (Word, Excel, PowerPoint) based on its filename
 */
export const isOfficeFile = (filename: string): boolean => {
	// Check by file extension
	const extension = filename.split('.').pop()?.toLowerCase();
	const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

	return extension ? officeExtensions.includes(extension) : false;
};

/**
 * Check if a file is a Word document based on its filename
 */
export const isWordFile = (filename: string): boolean => {
	// Check by file extension
	const extension = filename.split('.').pop()?.toLowerCase();
	return extension ? ['doc', 'docx'].includes(extension) : false;
};

/**
 * Check if a file is an Excel document based on its filename
 */
export const isExcelFile = (filename: string): boolean => {
	// Check by file extension
	const extension = filename.split('.').pop()?.toLowerCase();
	return extension ? ['xls', 'xlsx'].includes(extension) : false;
};

/**
 * Check if a file is a PowerPoint document based on its filename
 */
export const isPowerPointFile = (filename: string): boolean => {
	// Check by file extension
	const extension = filename.split('.').pop()?.toLowerCase();
	return extension ? ['ppt', 'pptx'].includes(extension) : false;
};

/**
 * Check if a file is an image based on its filename or file type
 */
export const isImageFile = (filename: string, fileType?: string): boolean => {
	// Check by file type first if available
	if (fileType) {
		return fileType.toLowerCase().startsWith('image/');
	}

	// Check by file extension
	const extension = filename.split('.').pop()?.toLowerCase();
	return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'svg', 'bmp'].includes(
		extension || ''
	);
};

/**
 * Check if a file is a video based on its filename or file type
 */
export const isVideoFile = (filename: string, fileType?: string): boolean => {
	// Check by file type first if available
	if (fileType) {
		return fileType.toLowerCase().startsWith('video/');
	}

	// Check by file extension
	const extension = filename.split('.').pop()?.toLowerCase();
	return ['mp4', 'webm', 'mkv', 'avi', 'mov', 'wmv', 'flv', 'm4v'].includes(extension || '');
};

/**
 * Check if a file is a document based on its filename or file type
 */
export const isDocumentFile = (filename: string, fileType?: string): boolean => {
	// Check by file type first if available
	if (fileType) {
		const type = fileType.toLowerCase();
		return (
			type.includes('document') ||
			type.includes('pdf') ||
			type.includes('word') ||
			type.includes('excel') ||
			type.includes('powerpoint') ||
			type.includes('text')
		);
	}

	// Check by file extension
	const extension = filename.split('.').pop()?.toLowerCase();
	return [
		'pdf',
		'doc',
		'docx',
		'xls',
		'xlsx',
		'ppt',
		'pptx',
		'txt',
		'rtf',
		'odt',
		'ods',
		'odp',
	].includes(extension || '');
};

/**
 * Get the file extension from a filename
 */
export const getFileExtension = (filename: string): string => {
	return filename.split('.').pop()?.toLowerCase() || '';
};

/**
 * Get a human-readable file type description
 */
export const getFileTypeDescription = (filename: string, fileType?: string): string => {
	if (isPdfFile(filename)) return 'PDF Document';
	if (isImageFile(filename, fileType)) return 'Image';
	if (isVideoFile(filename, fileType)) return 'Video';

	const extension = getFileExtension(filename).toUpperCase();
	if (extension) return `${extension} File`;

	return 'Document';
};

/**
 * Extract filename from URL by getting the last segment with extension
 * @param url - The URL to extract filename from
 * @returns Clean filename with extension, or fallback name if extraction fails
 * 
 * @example
 * extractFilenameFromUrl('https://example.com/folder/document.pdf') // Returns: 'document.pdf'
 * extractFilenameFromUrl('https://example.com/folder/image.jpg?v=123') // Returns: 'image.jpg'
 * extractFilenameFromUrl('https://example.com/folder/') // Returns: 'file'
 */
export const extractFilenameFromUrl = (url: string): string => {
	try {
		// Handle empty or invalid URLs
		if (!url || typeof url !== 'string') {
			return 'file';
		}

		// Remove query parameters and fragments
		const cleanUrl = url.split('?')[0].split('#')[0];
		
		// Extract the path part and get the last segment
		const urlPath = cleanUrl.split('/').pop() || '';
		
		// Decode URL encoding (e.g., %20 -> space)
		const decodedPath = decodeURIComponent(urlPath);
		
		// If we have a filename with extension, return it
		if (decodedPath && decodedPath.includes('.')) {
			// Clean up any remaining unwanted characters and ensure it's a valid filename
			const cleanFilename = decodedPath.replace(/[<>:"/\\|?*]/g, '_');
			return cleanFilename || 'file';
		}
		
		// If no extension found, return the path segment or fallback
		return decodedPath || 'file';
	} catch (error) {
		console.warn('Error extracting filename from URL:', error);
		return 'file';
	}
};

/**
 * Create a File object from a URL with extracted filename
 * @param url - The URL to create a file from
 * @param blob - The blob data for the file
 * @param fallbackName - Optional fallback name if extraction fails
 * @returns File object with clean filename extracted from URL
 */
export const createFileFromUrl = (url: string, blob: Blob, fallbackName?: string): File => {
	const filename = fallbackName || extractFilenameFromUrl(url);
	return new File([blob], filename, { type: blob.type });
};
